import React, {useEffect, useState} from "react";
import styled, {useTheme} from "styled-components";
import {useBoundStore} from "../../stores/useBoundStore";
import {useMeasure} from "react-use";
import ApplicationOneCells from "./ApplicationOneCells";
import {
  boxOneAnimation,
  boxTwoAnimation,
  boxThreeAnimation,
  boxFourAnimation,
  boxFiveAnimation,
  boxSixAnimation,
  boxSevenAnimation,
  boxEightAnimation,
  boxNineAnimation,
  boxTenAnimation,
  boxElevenAnimation,
  boxTwelveAnimation,
  boxThirteenAnimation,
  boxFourteenAnimation,
  boxFifteenAnimation,
  boxSixteenAnimation,
  columnFourAnimation,
  columnOneAnimation,
  columnThreeAnimation,
  columnTwoAnimation, rowFourAnimation, rowOneAnimation, rowThreeAnimation, rowTwoAnimation
} from "../../utils/animations";
import TextHolder from "../molecules/TextHolder";

const Holder = styled.div`
  width: 100%;
  aspect-ratio: 1;
  position: relative;
  overflow: hidden;

  .divForMeasuring {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }
`;

const Inner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: ${(props) => props.xPixelCols};
  grid-template-rows: ${(props) => props.yPixelRows};
  align-items: start;
  justify-items: start;
  background-color: var(--backgroundColor);

  .cell-inner {
    transform-origin: top left;
  }

  &.animateX,
  &.animateY,
  &.animateXY {
    grid-template-columns: repeat(4, 25%);
    grid-template-rows: repeat(4, 25%);
  }

  &.animateX {
    .cell-inner {
      &.col1 {
        animation: ${columnOneAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;
      }

      &.col2 {
        animation: ${columnTwoAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;
      }

      &.col3 {
        animation: ${columnThreeAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;
      }

      &.col4 {
        animation: ${columnFourAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;
      }
    }
  }

  &.animateY {
    .cell-inner {
      &.row1 {
        animation: ${rowOneAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;
      }

      &.row2 {
        animation: ${rowTwoAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;
      }

      &.row3 {
        animation: ${rowThreeAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;
      }

      &.row4 {
        animation: ${rowFourAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;
      }
    }
  }

  &.animateXY {
    .cell-inner {
      &.col1.row1 { animation: ${boxOneAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col2.row1 { animation: ${boxTwoAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col3.row1 { animation: ${boxThreeAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col4.row1 { animation: ${boxFourAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col1.row2 { animation: ${boxFiveAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col2.row2 { animation: ${boxSixAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col3.row2 { animation: ${boxSevenAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col4.row2 { animation: ${boxEightAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col1.row3 { animation: ${boxNineAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col2.row3 { animation: ${boxTenAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col3.row3 { animation: ${boxElevenAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col4.row3 { animation: ${boxTwelveAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col1.row4 { animation: ${boxThirteenAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col2.row4 { animation: ${boxFourteenAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col3.row4 { animation: ${boxFifteenAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
      &.col4.row4 { animation: ${boxSixteenAnimation} 10s cubic-bezier(0.01, 0.01, 0.01, 0.01) infinite;}
    }
  }
`;

function ApplicationOne(props) {
  const animation = useBoundStore((state) => state.animation);
  const xCols = useBoundStore((state) => state.xCols);
  const yRows = useBoundStore((state) => state.yRows);
  const background = useBoundStore((state) => state.background);
  const theme = useTheme();
  const [ref, {width, height}] = useMeasure();
  const [innerWidth, setInnerWidth] = useState(null);
  const [pixelXCols, setPixelXCols] = useState(null);
  const [innerHeight, setInnerHeight] = useState(null);
  const [pixelYRows, setPixelYRows] = useState(null);

  useEffect(() => {
    setPixelXCols(null);
    let holderWidth = width;
    let colWidths = xCols.map((col) => {
      return Math.floor((col / 100) * holderWidth);
    });
    let wholeHolderWidth = colWidths.reduce((a, b) => a + b, 0);
    setInnerWidth(wholeHolderWidth);
    setPixelXCols(colWidths.join("px ") + "px");
  }, [xCols, width, setInnerWidth, setPixelXCols]);

  useEffect(() => {
    setPixelYRows(null);
    let holderHeight = height;
    let rowHeights = yRows.map((row) => {
      return Math.floor((row / 100) * holderHeight);
    });
    let wholeHolderHeight = rowHeights.reduce((a, b) => a + b, 0);
    setInnerHeight(wholeHolderHeight);
    setPixelYRows(rowHeights.join("px ") + "px");
  }, [yRows, height, setInnerHeight, setPixelYRows]);

  return (
    <Holder>
      <div ref={ref} className="divForMeasuring" />
      <Inner
        width={innerWidth}
        height={innerHeight}
        xCols={xCols}
        yRows={yRows}
        xPixelCols={pixelXCols}
        yPixelRows={pixelYRows}
        className={animation && animation}
        style={{
          "--backgroundColor": theme.colors[background]
        }}
      >
        <ApplicationOneCells xCols={xCols} yRows={yRows} />
        <TextHolder />
      </Inner>
    </Holder>
  );
}

export default ApplicationOne;
